/**
 * Aura 3D Ring Viewer
 *
 * ESM module that mounts a Three.js scene into a host element.
 * - Loads /assets/aura-ring.glb when present, otherwise builds a procedural ring.
 * - Color swatches retint outer band material.
 * - Lazy mounts via IntersectionObserver, disposes when leaving viewport.
 * - Honors prefers-reduced-motion (no auto-rotate, no damping).
 * - WebGL fallback to a static thumbnail.
 *
 * Public API (returned by mountAuraViewer):
 *   { setColor(name), reset(), play(), pause(), dispose() }
 *
 * Three.js is loaded from a versioned CDN (r160). If the module imports fail,
 * the host gets a static <img> instead.
 */

/**
 * Imports use bare specifiers ('three', 'three/addons/...') resolved by an
 * import map declared in the section markup. See templates/index.liquid and
 * sections/pdp-3d-embed.liquid for the importmap block. Without the importmap
 * the browser cannot resolve the bare specifier and falls back to the static
 * thumbnail (which is what was happening before the importmap was added).
 */
const THREE_URL = 'three';
const ORBIT_URL = 'three/addons/controls/OrbitControls.js';
const GLTF_URL  = 'three/addons/loaders/GLTFLoader.js';
const ROOM_URL  = 'three/addons/environments/RoomEnvironment.js';

const COLOR_PRESETS = {
  black:  { color: 0x0e0e10, metalness: 0.85, roughness: 0.32, sheen: 0xffffff },
  silver: { color: 0xb8b8c0, metalness: 1.00, roughness: 0.18, sheen: 0xffffff },
  rose:   { color: 0xc69478, metalness: 0.95, roughness: 0.22, sheen: 0xffffff }
};

function isWebGLAvailable() {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch (e) { return false; }
}

function fallbackImage(host, src, alt) {
  host.innerHTML = '';
  const img = document.createElement('img');
  img.src = src;
  img.alt = alt || 'Aura ring';
  img.loading = 'lazy';
  img.decoding = 'async';
  img.style.cssText = 'width:100%;height:100%;object-fit:contain;';
  host.appendChild(img);
}

export async function mountAuraViewer(rootEl, options = {}) {
  if (!rootEl) return null;
  const fallbackSrc = options.fallback || rootEl.dataset.fallback || '/aura-thumb-square.jpg';
  if (!isWebGLAvailable()) {
    fallbackImage(rootEl, fallbackSrc);
    return null;
  }

  let THREE, OrbitControls, GLTFLoader, RoomEnvironment;
  try {
    THREE = await import(THREE_URL);
    ({ OrbitControls } = await import(ORBIT_URL));
    ({ GLTFLoader }    = await import(GLTF_URL));
    ({ RoomEnvironment } = await import(ROOM_URL));
  } catch (err) {
    console.warn('[aura-3d-viewer] Three.js load failed, falling back to image', err);
    fallbackImage(rootEl, fallbackSrc);
    return null;
  }

  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------- Scene ----------
  const scene = new THREE.Scene();
  scene.background = null;

  const w = Math.max(rootEl.clientWidth, 1);
  const h = Math.max(rootEl.clientHeight, 1);
  const camera = new THREE.PerspectiveCamera(35, w / h, 0.05, 50);
  camera.position.set(0, 0.45, 4.6);

  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(w, h, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.45;
  renderer.domElement.style.cssText = 'display:block;width:100%;height:100%;';

  rootEl.innerHTML = '';
  rootEl.style.position = rootEl.style.position || 'relative';
  rootEl.appendChild(renderer.domElement);

  // ---------- Environment (IBL via PMREM-baked RoomEnvironment) ----------
  // Metal materials need an environment map to read as metal — without it
  // they render almost black. RoomEnvironment is a built-in studio-style
  // procedural scene that gives clean, neutral reflections.
  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  const envTex = pmrem.fromScene(new RoomEnvironment(renderer), 0.04).texture;
  scene.environment = envTex;

  // ---------- Lights (on top of the IBL for shaped highlights) ----------
  const key = new THREE.DirectionalLight(0xffffff, 2.4);
  key.position.set(4, 6, 5);
  scene.add(key);

  const fill = new THREE.DirectionalLight(0xb8d8ff, 1.1);
  fill.position.set(-5, 1, 3);
  scene.add(fill);

  const rim = new THREE.DirectionalLight(0xffe2c0, 1.6);
  rim.position.set(-3, 4, -5);
  scene.add(rim);

  // Soft top-down area-style light to lift the inside of the band
  const top = new THREE.DirectionalLight(0xffffff, 0.55);
  top.position.set(0, 6, 1);
  scene.add(top);

  scene.add(new THREE.AmbientLight(0xffffff, 0.5));

  const hemi = new THREE.HemisphereLight(0xfff4e0, 0x202830, 0.85);
  scene.add(hemi);

  // ---------- Ring Group ----------
  const ringGroup = new THREE.Group();
  scene.add(ringGroup);

  // Outer band material — what swatches retint
  let outerMat = new THREE.MeshPhysicalMaterial({
    color: COLOR_PRESETS.black.color,
    metalness: COLOR_PRESETS.black.metalness,
    roughness: COLOR_PRESETS.black.roughness,
    clearcoat: 0.65,
    clearcoatRoughness: 0.18,
    envMapIntensity: 1.15,
    sheen: 0.22,
    sheenColor: new THREE.Color(0xffffff),
    sheenRoughness: 0.4
  });

  let glbRoot = null;
  let proceduralRoot = null;

  function buildProcedural() {
    const grp = new THREE.Group();

    // Outer band — main visible torus
    const outer = new THREE.Mesh(
      new THREE.TorusGeometry(1.0, 0.18, 64, 160),
      outerMat
    );
    outer.castShadow = true;
    grp.add(outer);

    // Inner polished steel band
    const innerMat = new THREE.MeshPhysicalMaterial({
      color: 0xb8b8c0,
      metalness: 1.0,
      roughness: 0.16,
      clearcoat: 0.4
    });
    const inner = new THREE.Mesh(
      new THREE.TorusGeometry(0.84, 0.04, 32, 128),
      innerMat
    );
    grp.add(inner);

    // Three sensor patches glowing at the inside surface
    const sensorMat = new THREE.MeshStandardMaterial({
      color: 0x0a1e15,
      emissive: 0x33d49a,
      emissiveIntensity: 0.55,
      metalness: 0.2,
      roughness: 0.4
    });

    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2 + Math.PI / 2;
      const sensor = new THREE.Mesh(
        new THREE.BoxGeometry(0.10, 0.06, 0.04),
        sensorMat
      );
      sensor.position.set(Math.cos(a) * 0.84, Math.sin(a) * 0.84, 0);
      sensor.lookAt(0, 0, 0);
      grp.add(sensor);
    }

    // Tiny chip plate on the inner band
    const chip = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.08, 0.02),
      new THREE.MeshStandardMaterial({ color: 0x232830, metalness: 0.5, roughness: 0.5 })
    );
    chip.position.set(0, -0.84, 0);
    chip.rotation.z = Math.PI / 2;
    grp.add(chip);

    // Tilt slightly so the inside is partly visible
    grp.rotation.x = Math.PI * 0.16;
    return grp;
  }

  // Try GLB first, fall back to procedural
  const glbUrl = options.glbUrl || rootEl.dataset.glb;
  let usingGlb = false;

  function applyOuterColorToGlb(root) {
    root.traverse((node) => {
      if (!node.isMesh || !node.material) return;
      const mats = Array.isArray(node.material) ? node.material : [node.material];
      mats.forEach(m => {
        // Leave glowing sensor / LED materials alone — keep their emissive identity.
        const emits = m.emissive
          && (m.emissiveIntensity === undefined || m.emissiveIntensity > 0.05)
          && typeof m.emissive.getHex === 'function'
          && m.emissive.getHex() !== 0x000000;
        if (emits) return;

        // The Meshy export bakes the band's rose-gold albedo into a texture
        // `map`, so tinting m.color alone is multiplied away by that texture and
        // the swatches appear to do nothing. Drop the diffuse map and let the
        // PBR base color + IBL reflections drive the metal so black/silver/rose
        // actually swap. (normalMap / roughnessMap, if any, are preserved.)
        if (m.map) { m.map = null; }
        if (m.color && m.color.setHex) m.color.setHex(outerMat.color.getHex());
        if (m.metalness !== undefined) m.metalness = outerMat.metalness;
        if (m.roughness !== undefined) m.roughness = outerMat.roughness;
        if ('clearcoat' in m) { m.clearcoat = 0.5; m.clearcoatRoughness = 0.2; }
        m.envMapIntensity = 1.6;
        m.needsUpdate = true;
      });
    });
  }

  if (glbUrl) {
    try {
      const loader = new GLTFLoader();
      const gltf = await new Promise((resolve, reject) => {
        loader.load(glbUrl, resolve, undefined, reject);
      });
      glbRoot = gltf.scene;

      // Center + scale so it fits in our camera frame
      const box = new THREE.Box3().setFromObject(glbRoot);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);
      glbRoot.position.sub(center);
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      const scale = 2.4 / maxDim;
      glbRoot.scale.setScalar(scale);
      glbRoot.rotation.x = 0;

      // Polish materials — IBL-driven, so envMapIntensity does the heavy lifting.
      glbRoot.traverse((node) => {
        if (node.isMesh) {
          node.castShadow = true;
          node.receiveShadow = false;
          if (node.material) {
            const mats = Array.isArray(node.material) ? node.material : [node.material];
            mats.forEach(m => {
              if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
              m.envMapIntensity = 1.6;
              // Many GLB exports clamp metalness/roughness too aggressively.
              // Open up the response so the rose-gold actually reads as metal.
              if (m.metalness !== undefined && m.metalness >= 0.2) {
                m.metalness = Math.min(1, m.metalness * 1.05);
              }
              if (m.roughness !== undefined) {
                m.roughness = Math.max(0.12, Math.min(0.55, m.roughness));
              }
              m.needsUpdate = true;
            });
          }
        }
      });

      ringGroup.add(glbRoot);
      usingGlb = true;
    } catch (err) {
      console.warn('[aura-3d-viewer] GLB load failed, using procedural ring', err);
    }
  }

  if (!usingGlb) {
    proceduralRoot = buildProcedural();
    ringGroup.add(proceduralRoot);
  }

  // ---------- Controls ----------
  const scrollDrive = options.scrollDrive ?? rootEl.dataset.scrollDrive === "true";
  // Pinned scrollytelling: host sits inside a tall [data-scrolly] sticky stage.
  // Determined up front so we can disable drag-override (the wheel/touch that
  // scrolls the page IS what drives the ring — it must not pause itself).
  const pinEl = scrollDrive ? rootEl.closest('[data-scrolly]') : null;
  const pinned = !!pinEl;
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableZoom = false;
  controls.enablePan = false;
  controls.enableRotate = !pinned;           // pinned sections are scroll-only
  controls.dampingFactor = reduceMotion ? 0.0 : 0.08;
  controls.enableDamping = !reduceMotion;
  controls.autoRotate = !reduceMotion && !scrollDrive;
  controls.autoRotateSpeed = 0.55;
  controls.minPolarAngle = Math.PI * 0.18;
  controls.maxPolarAngle = Math.PI * 0.82;

  // Pause auto-rotate on user interaction, resume after 4s idle (skipped in scrollDrive mode).
  let idleTimer = null;
  let userOverride = false;   // true while user is dragging — pauses scroll-drive
  let userOverrideTimer = null;
  function bumpIdle() {
    if (scrollDrive) {
      userOverride = true;
      if (userOverrideTimer) clearTimeout(userOverrideTimer);
      userOverrideTimer = setTimeout(() => { userOverride = false; }, 2500);
      return;
    }
    controls.autoRotate = false;
    if (idleTimer) clearTimeout(idleTimer);
    if (!reduceMotion) {
      idleTimer = setTimeout(() => { controls.autoRotate = true; }, 4000);
    }
  }
  // In pinned mode, only an explicit drag (pointerdown) should ever override —
  // never wheel/touch scroll, which is the driver itself.
  (pinned ? ['pointerdown'] : ['pointerdown','wheel','touchstart','keydown']).forEach(evt => {
    renderer.domElement.addEventListener(evt, bumpIdle, { passive: true });
  });

  // ---------- Scroll-drive: ring Y-rotation tracks viewport progress ----------
  // Reversible — remove `data-scroll-drive="true"` from the host element to disable.
  // We blend the scroll-driven target with the controls-managed rotation so a
  // user drag still feels natural (override window: 2.5s).
  let scrollAngle = 0;
  let scrollTargetAngle = 0;
  // Pinned "scrollytelling" mode (pinEl / pinned computed in Controls above):
  // the ring rotates + scales + tilts across the container's scroll progress.
  let scrollScale = pinned ? 0.82 : 1;
  let scrollTargetScale = scrollScale;
  let scrollTilt = 0;
  let scrollTiltTarget = 0;
  function updateScrollAngle() {
    if (!scrollDrive) return;
    const winH = window.innerHeight || 800;
    if (pinned) {
      // Progress 0..1 across the tall pinned container.
      const pr = pinEl.getBoundingClientRect();
      const total = pr.height - winH;
      const p = total > 0 ? Math.min(1, Math.max(0, -pr.top / total)) : 0;
      scrollTargetAngle = p * Math.PI * 3;             // ~1.5 turns over the section
      scrollTargetScale = 0.82 + p * 0.40;             // grows 0.82 → 1.22
      scrollTiltTarget  = Math.sin(p * Math.PI) * 0.45; // dips then returns
      pinEl.style.setProperty('--scrolly-p', p.toFixed(4));
      return;
    }
    const r = rootEl.getBoundingClientRect();
    // Progress: -1 above viewport, 0 entering, 1 leaving — clamped to a useful band.
    const p = 1 - (r.top + r.height * 0.5) / winH;
    const t = Math.max(-0.5, Math.min(1.5, p));
    // ~2 full rotations as the section travels through the viewport.
    scrollTargetAngle = t * Math.PI * 4;
  }
  if (scrollDrive) {
    window.addEventListener("scroll", updateScrollAngle, { passive: true });
    updateScrollAngle();
  }

  // ---------- Resize ----------
  const ro = new ResizeObserver(() => {
    const cw = Math.max(rootEl.clientWidth, 1);
    const ch = Math.max(rootEl.clientHeight, 1);
    renderer.setSize(cw, ch, false);
    camera.aspect = cw / ch;
    camera.updateProjectionMatrix();
  });
  ro.observe(rootEl);

  // ---------- Render loop ----------
  let running = true;
  let rafId = null;
  function tick() {
    if (!running) return;
    controls.update();
    if (scrollDrive && !userOverride) {
      // Smoothly lerp the ring's Y-rotation toward the scroll target. Ease
      // factor 0.09 gives a Lenis-like feel without runaway lag.
      scrollAngle += (scrollTargetAngle - scrollAngle) * 0.09;
      ringGroup.rotation.y = scrollAngle;
      if (pinned) {
        scrollScale += (scrollTargetScale - scrollScale) * 0.09;
        scrollTilt  += (scrollTiltTarget - scrollTilt) * 0.09;
        ringGroup.scale.setScalar(scrollScale);
        ringGroup.rotation.x = scrollTilt;
      }
    }
    renderer.render(scene, camera);
    rafId = requestAnimationFrame(tick);
  }
  rafId = requestAnimationFrame(tick);

  // ---------- API ----------
  function setColor(name) {
    const preset = COLOR_PRESETS[name] || COLOR_PRESETS.black;
    outerMat.color.setHex(preset.color);
    outerMat.metalness = preset.metalness;
    outerMat.roughness = preset.roughness;
    outerMat.needsUpdate = true;
    if (usingGlb && glbRoot) applyOuterColorToGlb(glbRoot);
  }
  if (options.color) setColor(options.color);

  function play() {
    if (running) return;
    running = true;
    rafId = requestAnimationFrame(tick);
  }
  function pause() {
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
  }
  function reset() {
    camera.position.set(0, 0.45, 4.6);
    controls.target.set(0, 0, 0);
    ringGroup.rotation.set(usingGlb ? 0 : Math.PI * 0.16, 0, 0);
    controls.update();
  }
  function dispose() {
    pause();
    ro.disconnect();
    if (idleTimer) clearTimeout(idleTimer);
    if (userOverrideTimer) clearTimeout(userOverrideTimer);
    if (scrollDrive) window.removeEventListener("scroll", updateScrollAngle);
    controls.dispose();
    scene.traverse((obj) => {
      if (obj.geometry) obj.geometry.dispose?.();
      if (obj.material) {
        if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose?.());
        else obj.material.dispose?.();
      }
    });
    renderer.dispose();
    if (renderer.domElement && renderer.domElement.parentNode) {
      renderer.domElement.parentNode.removeChild(renderer.domElement);
    }
  }

  return { setColor, reset, play, pause, dispose };
}

/**
 * Convenience boot: finds [data-aura-3d] hosts, lazy-mounts when scrolled into view,
 * disposes when scrolled out (saves battery on mobile).
 *
 * Usage: <div data-aura-3d data-glb="/assets/aura-ring.glb" data-color="black"></div>
 */
export function bootAuraViewers() {
  const hosts = document.querySelectorAll('[data-aura-3d]');
  if (!hosts.length) return;

  const apis = new WeakMap();

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const host = entry.target;
      if (entry.isIntersecting && !apis.has(host)) {
        const api = mountAuraViewer(host, {
          glbUrl: host.dataset.glb || undefined,
          // No data-color => keep the model's ORIGINAL baked materials (no retint).
          // Hosts that want a specific finish (e.g. the PDP) still set data-color.
          color: host.dataset.color || undefined,
          fallback: host.dataset.fallback
        });
        apis.set(host, api);
      } else if (!entry.isIntersecting && apis.has(host)) {
        const api = apis.get(host);
        if (api && typeof api.then === 'function') {
          api.then(a => a && a.dispose && a.dispose());
        } else if (api && api.dispose) {
          api.dispose();
        }
        apis.delete(host);
      }
    });
  }, { rootMargin: '200px 0px' });

  hosts.forEach(h => io.observe(h));

  // Wire color swatches to the nearest viewer. Stage-centric so it works in
  // any layout — the PDP `.ring3d` section, the homepage pinned scrolly, etc.
  // Supports both legacy `.ring3d__sw` and the generic `[data-aura-sw]` buttons.
  const SW_SEL = '.ring3d__sw, [data-aura-sw]';
  hosts.forEach((stage) => {
    const section = stage.closest('[data-aura-section]') || stage.closest('section') || stage.parentElement;
    if (!section) return;
    const swatches = section.querySelectorAll(SW_SEL);
    swatches.forEach((btn) => {
      btn.addEventListener('click', async () => {
        swatches.forEach(b => b.setAttribute('aria-selected', String(b === btn)));
        const apiOrPromise = apis.get(stage);
        if (!apiOrPromise) return;
        const api = (apiOrPromise && typeof apiOrPromise.then === 'function')
          ? await apiOrPromise
          : apiOrPromise;
        api && api.setColor && api.setColor(btn.dataset.color || 'black');
      });
    });
  });
}

// Auto-boot on DOM ready
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAuraViewers);
  } else {
    bootAuraViewers();
  }
}
